import React from 'react';

interface Device {
  id: number;
  name: string;
  status: 0 | 1;
  socketConnected?: boolean;
  deviceTypes?: {
    id: number;
    name: string;
  };
  settings?: {
    cameraStreamEndpoint?: string;
    [key: string]: any;
  };
}

interface CameraDeviceCardProps {
  device: Device;
  themeColor?: string;
  onCaptureClick?: (device: Device) => void;
}

/**
 * Card component for camera device
 * Shows camera stream when online
 */
const CameraDeviceCard: React.FC<CameraDeviceCardProps> = ({
  device,
  themeColor = '#9c27b0',
  onCaptureClick,
}) => {
  const streamUrl = device.settings?.cameraStreamEndpoint;
  const isOnline = device.socketConnected === true;

  return (
    <div style={{
      background: 'linear-gradient(135deg, #171a1c 0%, #1a1f22 100%)',
      border: `1px solid ${themeColor}30`,
      borderRadius: '12px',
      overflow: 'hidden',
      minWidth: '280px',
    }}>
      {/* Card Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '1rem',
        borderBottom: `1px solid ${themeColor}20`,
      }}>
        <div style={{
          fontSize: '1.5rem',
        }}>
          📷
        </div>
        <div style={{
          width: '10px',
          height: '10px',
          borderRadius: '50%',
          backgroundColor: isOnline ? '#00ff88' : '#ff3366',
          boxShadow: isOnline
            ? '0 0 8px #00ff88'
            : '0 0 8px #ff3366',
        }} />
        <div>
          <div style={{
            fontSize: '0.9rem',
            fontWeight: 600,
            color: '#fff',
          }}>
            {device.name}
          </div>
          <div style={{
            fontSize: '0.7rem',
            color: '#6c757d',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}>
            {device.deviceTypes?.name || 'Camera'}
          </div>
        </div>
      </div>

      {/* Stream or Placeholder */}
      <div style={{
        aspectRatio: '16/9',
        background: '#0d1012',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
      }}>
        {!isOnline ? (
          <div style={{
            textAlign: 'center',
            color: '#dc3545',
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem', opacity: 0.5 }}>📷</div>
            <div style={{ fontSize: '0.9rem', fontWeight: 500 }}>Camera Offline</div>
            <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.25rem' }}>
              Waiting for connection...
            </div>
          </div>
        ) : !streamUrl ? (
          <div style={{
            textAlign: 'center',
            color: '#6c757d',
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem', opacity: 0.5 }}>📷</div>
            <div style={{ fontSize: '0.9rem' }}>No stream configured</div>
            <div style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
              Set cameraStreamEndpoint in device settings
            </div>
          </div>
        ) : (
          <>
            <img
              src={streamUrl}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
              alt={`${device.name} stream`}
            />
            {/* Live indicator */}
            <div style={{
              position: 'absolute',
              top: '0.75rem',
              left: '0.75rem',
              background: 'rgba(220, 53, 69, 0.9)',
              padding: '0.25rem 0.5rem',
              borderRadius: '4px',
              fontSize: '0.65rem',
              fontWeight: 700,
              color: '#fff',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}>
              <span style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: '#fff',
                animation: 'pulse 1s ease-in-out infinite',
              }} />
              LIVE
            </div>
            {/* Capture button */}
            {onCaptureClick && (
              <button
                onClick={() => onCaptureClick(device)}
                style={{
                  position: 'absolute',
                  top: '0.75rem',
                  right: '0.75rem',
                  background: `linear-gradient(135deg, ${themeColor}80 0%, ${themeColor}60 100%)`,
                  border: `1px solid ${themeColor}`,
                  borderRadius: '6px',
                  padding: '0.4rem 0.75rem',
                  color: '#fff',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.2s ease',
                  boxShadow: `0 2px 8px ${themeColor}40`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}80 100%)`;
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = `linear-gradient(135deg, ${themeColor}80 0%, ${themeColor}60 100%)`;
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                📷 CAPTURE
              </button>
            )}
          </>
        )}

        {/* Capture button when offline - shown disabled */}
        {onCaptureClick && !isOnline && (
          <button
            disabled
            style={{
              position: 'absolute',
              top: '0.75rem',
              right: '0.75rem',
              background: 'rgba(108, 117, 125, 0.3)',
              border: '1px solid #6c757d',
              borderRadius: '6px',
              padding: '0.4rem 0.75rem',
              color: '#6c757d',
              fontSize: '0.7rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              cursor: 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              opacity: 0.6,
            }}
          >
            📷 CAPTURE
          </button>
        )}
      </div>
    </div>
  );
};

export default CameraDeviceCard;
